'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import TagManager from 'react-gtm-module'

import { env } from '@/env'
import { usePathname } from 'next/navigation'
import { getSession } from '@/lib/auth'

export enum ButtonEvents {
  VIEW_CUPOM = 'button_click_viewcupom', // READY
  PEGAR_CUPOM = 'button_click_pegarcupom', // READY
  LOGIN_ACTION = 'button_click_login', // READY
  LOGOUT_ACTION = 'button_click_logout', // READY
  PRODUCT_VIEW = 'button_click_product_view', // READY
  CALL_TO_US = 'button_click_calltous', // READY
  FILTER_CATEGORY = 'button_click_filter_category', // READY

  LOGIN_ACTION_PHONE = 'button_click_login_phone', // READY
  LOGIN_ACTION_PHONE_CONTINUAR = 'button_click_login_phone_continuar', // READY
  FINISH_LOGIN_ACTION_SMS = 'button_click_login_sms_continuar', // READY
  REGISTER_CONTINUE = 'button_click_register_user', // READY
  LOGIN_ACTION_TROUBLESHOOTING = 'button_click_login_troubleshooting', // READY
  TABELA_NUTRICIONAL = 'button_click_tabela_nutricional', // READY
}

export enum ExtraEvents {
  SUCCESS_LOGIN = 'successfully_logged_in', // READY
  SUCCESS_REGISTRED = 'successfully_registred_in', // READY
}

interface ITagManagerContextType {
  sendPageViewEvent: (pagePath: string, pageTitle: string) => void
  sendButtonClickEvent: (event: ButtonEvents, eventData?: object) => void
  sendExtraEvents: (event: ExtraEvents, eventData?: object) => void
}

const TagManagerContext = createContext<ITagManagerContextType | undefined>(
  undefined,
)

interface ITagManagerProviderProps {
  children: React.ReactNode
}

export const GoogleTagManagerProvider: React.FC<ITagManagerProviderProps> = ({
  children,
}: ITagManagerProviderProps) => {
  const [userId, setUserId] = useState<string>()
  const pathname = usePathname()

  const sendPageViewEvent = useCallback(
    (pagePath: string, pageTitle: string) => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'page_view',
          pagePath,
          pageTitle,
          userId,
        },
      })
    },
    [userId],
  )

  useEffect(() => {
    const loadSession = async () => {
      const session = await getSession()
      if (session) {
        setUserId(session.customerId)
      }
    }
    loadSession()
  }, [])

  useEffect(() => {
    TagManager.initialize({
      gtmId: env.NEXT_PUBLIC_GTM_ID,
    })

    if (typeof window !== 'undefined') {
      const pagePath = pathname // Get the current route path
      const pageTitle = document.title // Get the current page title

      sendPageViewEvent(pagePath, pageTitle)
    }
  }, [pathname, sendPageViewEvent])

  const sendButtonClickEvent = useCallback(
    (event: ButtonEvents, eventData?: object) => {
      TagManager.dataLayer({
        dataLayer: {
          event,
          ...eventData,
          userId,
        },
      })
    },
    [userId],
  )

  const sendExtraEvents = useCallback(
    (event: ExtraEvents, eventData?: object) => {
      TagManager.dataLayer({
        dataLayer: {
          event,
          ...eventData,
          userId,
        },
      })
    },
    [userId],
  )

  const contextValue: ITagManagerContextType = {
    sendPageViewEvent,
    sendButtonClickEvent,
    sendExtraEvents,
  }

  return (
    <TagManagerContext.Provider value={contextValue}>
      {children}
    </TagManagerContext.Provider>
  )
}

export const useTagManager = (): ITagManagerContextType => {
  const context = useContext(TagManagerContext)
  if (!context) {
    throw new Error('useTagManager must be used within a TagManagerProvider')
  }
  return context
}
